import { useEffect, useId, useRef } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { AlertCircle, Loader2, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { CLASES_TONO, type Etiqueta, type Tono } from "../vocabulario.ts";

export const cx = (...partes: (string | false | null | undefined)[]) =>
  partes.filter(Boolean).join(" ");

// ─────────────────────────── botón ───────────────────────────

type Variante = "primario" | "suave" | "fantasma" | "peligro" | "exito";

const VARIANTES: Record<Variante, string> = {
  primario:
    "bg-marca text-white shadow-plano hover:bg-marca-fuerte active:scale-[.98] disabled:bg-marca/50",
  suave:
    "bg-superficie-2 text-tinta border border-borde hover:border-borde-fuerte hover:bg-superficie active:scale-[.98]",
  fantasma: "text-tenue hover:text-tinta hover:bg-superficie-2 active:scale-[.98]",
  exito:
    "bg-exito text-white shadow-plano hover:brightness-110 active:scale-[.98] disabled:opacity-50",
  peligro:
    "bg-peligro-suave text-peligro-tinta border border-peligro/25 hover:bg-peligro hover:text-white active:scale-[.98]",
};

const TAMANOS = {
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-lg",
  md: "h-10 px-4 text-sm gap-2 rounded-xl",
  lg: "h-12 px-5 text-[15px] gap-2 rounded-xl",
} as const;

interface PropsBoton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  tamano?: keyof typeof TAMANOS;
  icono?: LucideIcon;
  cargando?: boolean;
  bloque?: boolean;
}

export function Boton({
  variante = "suave",
  tamano = "md",
  icono: Icono,
  cargando,
  bloque,
  className,
  children,
  disabled,
  ...resto
}: PropsBoton) {
  return (
    <button
      {...resto}
      disabled={disabled || cargando}
      className={cx(
        "inline-flex items-center justify-center font-medium whitespace-nowrap",
        "transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100",
        VARIANTES[variante],
        TAMANOS[tamano],
        bloque && "w-full",
        className,
      )}
    >
      {cargando ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        Icono && <Icono className="size-4 shrink-0" aria-hidden />
      )}
      {children}
    </button>
  );
}

// ─────────────────────────── insignias ───────────────────────────

export function Insignia({
  tono = "neutro",
  icono: Icono,
  children,
  titulo,
  className,
}: {
  tono?: Tono;
  icono?: LucideIcon;
  children: ReactNode;
  titulo?: string;
  className?: string;
}) {
  const c = CLASES_TONO[tono];
  return (
    <span
      title={titulo}
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap",
        c.fondo,
        c.texto,
        c.borde,
        className,
      )}
    >
      {Icono && <Icono className="size-3.5 shrink-0" aria-hidden />}
      {children}
    </span>
  );
}

/** Insignia construida desde el vocabulario: el texto y el porqué van juntos. */
export function InsigniaDe({ etiqueta, className }: { etiqueta: Etiqueta; className?: string }) {
  return (
    <Insignia
      tono={etiqueta.tono}
      icono={etiqueta.icono}
      titulo={etiqueta.explica}
      className={className}
    >
      {etiqueta.texto}
    </Insignia>
  );
}

// ─────────────────────────── contenedores ───────────────────────────

export function Tarjeta({
  children,
  className,
  ...resto
}: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...resto} className={cx("tarjeta", className)}>
      {children}
    </div>
  );
}

export function Seccion({
  titulo,
  descripcion,
  accion,
  children,
  className,
}: {
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("space-y-4", className)}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{titulo}</h2>
          {/* `max-w-prose` aquí y no en el marco: al ensanchar la página para que
              las tablas respiren, los textos pasaron a medir 1264px — unos 150
              caracteres por renglón, donde el ojo pierde el salto de línea. Una
              tabla quiere todo el ancho; un párrafo, no. */}
          {descripcion && <p className="mt-0.5 max-w-prose text-sm text-tenue">{descripcion}</p>}
        </div>
        {accion}
      </header>
      {children}
    </section>
  );
}

// ─────────────────────────── formularios ───────────────────────────

interface PropsCampo extends InputHTMLAttributes<HTMLInputElement> {
  etiqueta: string;
  ayuda?: string;
  error?: string;
  icono?: LucideIcon;
}

export function Campo({ etiqueta, ayuda, error, icono: Icono, className, ...resto }: PropsCampo) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-tinta">
        {etiqueta}
      </label>
      <div className="relative">
        {Icono && (
          <Icono
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue-2"
            aria-hidden
          />
        )}
        <input
          {...resto}
          id={id}
          aria-invalid={!!error}
          aria-describedby={ayuda || error ? `${id}-ayuda` : undefined}
          className={cx(
            "h-11 w-full rounded-xl border bg-superficie px-3.5 text-sm text-tinta",
            "placeholder:text-tenue-2 transition-colors",
            "focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none",
            Icono && "pl-10",
            error ? "border-peligro/60" : "border-borde hover:border-borde-fuerte",
            className,
          )}
        />
      </div>
      {(ayuda || error) && (
        <p id={`${id}-ayuda`} className={cx("max-w-prose text-xs", error ? "text-peligro-tinta" : "text-tenue")}>
          {error ?? ayuda}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────── mensajes ───────────────────────────

export function Aviso({
  tono = "peligro",
  titulo,
  children,
  icono: Icono = AlertCircle,
}: {
  tono?: Tono;
  titulo?: string;
  children?: ReactNode;
  icono?: LucideIcon;
}) {
  const c = CLASES_TONO[tono];
  return (
    <div
      role="alert"
      className={cx("flex gap-3 rounded-xl border p-3.5 text-sm", c.fondo, c.borde, c.texto)}
    >
      <Icono className="mt-0.5 size-4.5 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-1">
        {titulo && <p className="font-semibold">{titulo}</p>}
        {children && <div className="leading-relaxed opacity-90">{children}</div>}
      </div>
    </div>
  );
}

export function Vacio({
  icono: Icono,
  titulo,
  children,
  accion,
}: {
  icono: LucideIcon;
  titulo: string;
  children?: ReactNode;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="grid size-14 place-items-center rounded-2xl bg-marca-suave text-marca-tinta">
        <Icono className="size-6" aria-hidden />
      </div>
      <div className="space-y-1">
        <p className="font-semibold">{titulo}</p>
        {children && <p className="mx-auto max-w-sm text-sm text-tenue">{children}</p>}
      </div>
      {accion}
    </div>
  );
}

// ─────────────────────────── espera ───────────────────────────

export const Esqueleto = ({ className }: { className?: string }) => (
  <div className={cx("veta rounded-lg bg-superficie-2", className)} aria-hidden />
);

export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 py-12 text-sm text-tenue">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{texto}</span>
    </div>
  );
}

// ─────────────────────────── diálogo ───────────────────────────

/**
 * Diálogo nativo: `<dialog>` trae gratis el foco atrapado, `Escape` y la capa
 * superior. Reimplementarlo con divs es la vía rápida a un modal que un lector
 * de pantalla no ve.
 */
export function Dialogo({
  abierto,
  alCerrar,
  titulo,
  descripcion,
  children,
  pie,
  ancho = "max-w-lg",
}: {
  abierto: boolean;
  alCerrar: () => void;
  titulo: string;
  descripcion?: string;
  children?: ReactNode;
  pie?: ReactNode;
  ancho?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) d.showModal();
    if (!abierto && d.open) d.close();
  }, [abierto]);

  return (
    <dialog
      ref={ref}
      onClose={alCerrar}
      onClick={(e) => e.target === ref.current && alCerrar()}
      className={cx(
        // `m-auto` no es cosmético: el reset de Tailwind pone `margin: 0` en
        // todo, y sin recuperarlo el diálogo modal se pega a la esquina
        // superior izquierda en lugar de centrarse.
        "m-auto max-h-[85dvh] w-[calc(100vw-2rem)] overflow-y-auto",
        "rounded-2xl border border-borde bg-superficie p-0 text-tinta shadow-flotante",
        "backdrop:bg-black/45 backdrop:backdrop-blur-sm open:subir",
        ancho,
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-borde p-5">
        <div className="space-y-1">
          <h2 className="text-base font-semibold tracking-tight">{titulo}</h2>
          {descripcion && <p className="text-sm text-tenue">{descripcion}</p>}
        </div>
        <button
          onClick={alCerrar}
          aria-label="Cerrar"
          className="-m-1 rounded-lg p-1 text-tenue transition-colors hover:bg-superficie-2 hover:text-tinta"
        >
          <X className="size-4.5" aria-hidden />
        </button>
      </div>
      {children && <div className="p-5">{children}</div>}
      {pie && (
        <div className="flex justify-end gap-2 border-t border-borde bg-superficie-2/60 p-4">
          {pie}
        </div>
      )}
    </dialog>
  );
}

// ─────────────────────────── varios ───────────────────────────

/** Barra de progreso semántica. Se usa para cartera y para automatización. */
export function Barra({
  fraccion,
  tono = "marca",
  className,
}: {
  fraccion: number;
  tono?: Tono;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(fraccion) ? fraccion : 0)) * 100;
  return (
    <div className={cx("h-1.5 w-full overflow-hidden rounded-full bg-superficie-2", className)}>
      <div
        className={cx("h-full rounded-full transition-[width] duration-700", CLASES_TONO[tono].punto)}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
